import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { uploadFile, UPLOAD_FOLDERS } from '../../services/uploadService';
import newsService from './newsService';
import {
  buildProductSelectOptions,
  ProductSelectOption,
  STATUS_COLOR,
} from './news.helpers';

export default function NewsEditDrawer({
  open,
  article,
  products,
  onClose,
  onSaved,
  permissions,
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');

  const productOptions = useMemo(
    () => buildProductSelectOptions(products),
    [products],
  );

  useEffect(() => {
    if (article) {
      form.setFieldsValue({
        headline: article.headline,
        short_description: article.short_description,
        share_short_name: article.share_short_name,
        logo_url: article.logo_url,
        source_name: article.source_name,
        published_at: article.published_at ? dayjs(article.published_at) : null,
      });
      setLogoPreview(article.logo_url || '');
    }
  }, [article, form]);

  const runAction = async (key, fn) => {
    setActionLoading(key);
    try {
      await fn();
      message.success('Updated.');
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Action failed.');
    } finally {
      setActionLoading('');
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await newsService.updateNews(article.id, {
        ...values,
        published_at: values.published_at
          ? values.published_at.toISOString()
          : null,
      });
      message.success('News saved.');
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleShareSelect = (shortName) => {
    const match = products.find(
      (p) => p.short_name?.toLowerCase() === String(shortName || '').toLowerCase(),
    );
    if (match?.logo_url && !article?.logo_manual_override) {
      setLogoPreview(match.logo_url);
    } else if (!shortName) {
      setLogoPreview(form.getFieldValue('logo_url') || '');
    }
  };

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const result = await uploadFile(file, UPLOAD_FOLDERS.LOGOS);
      if (!result.success) {
        message.error(result.message || 'Logo upload failed.');
        return false;
      }
      form.setFieldsValue({ logo_url: result.url });
      setLogoPreview(result.url);
      await newsService.updateNews(article.id, { logo_url: result.url });
      message.success('Logo uploaded.');
    } catch (err) {
      message.error(err?.message || 'Logo upload failed.');
    } finally {
      setUploadingLogo(false);
    }
    return false;
  };

  const handleSyncLogo = async () => {
    setActionLoading('sync');
    try {
      const updated = await newsService.syncLogo(article.id);
      form.setFieldsValue({ logo_url: updated.logo_url });
      setLogoPreview(updated.logo_url || '');
      message.success('Logo synced from product.');
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Sync failed.');
    } finally {
      setActionLoading('');
    }
  };

  const drawerTitle = (
    <Space>
      <span>Edit news article</span>
      {article?.status ? (
        <Tag color={STATUS_COLOR[article.status] || 'default'}>{article.status}</Tag>
      ) : null}
    </Space>
  );

  return (
    <Drawer
      title={drawerTitle}
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          {permissions.canEdit ? (
            <Button loading={saving} type="primary" onClick={handleSave}>
              Save
            </Button>
          ) : null}
        </Space>
      }
    >
      {article?.canonical_url ? (
        <p className="mb-4 text-sm text-gray-500">
          Source:{' '}
          <a href={article.canonical_url} target="_blank" rel="noreferrer">
            {article.canonical_url}
          </a>
        </p>
      ) : null}

      <Form form={form} layout="vertical">
        <Form.Item name="share_short_name" label="Share">
          <Select
            showSearch
            allowClear
            placeholder="Select share"
            onChange={handleShareSelect}
            optionFilterProp="label"
            options={productOptions}
            optionRender={(option) => (
              <ProductSelectOption product={option.data.product} />
            )}
            disabled={!permissions.canEdit}
          />
        </Form.Item>

        <Form.Item name="logo_url" label="Logo URL">
          <Input
            placeholder="https://..."
            disabled={!permissions.canEdit}
            onChange={(e) => setLogoPreview(e.target.value)}
          />
        </Form.Item>

        {logoPreview ? (
          <div className="mb-4">
            <img
              src={logoPreview}
              alt="Logo preview"
              className="h-16 w-16 rounded object-cover border border-gray-200"
            />
          </div>
        ) : null}

        {permissions.canEdit ? (
          <Space wrap className="mb-4">
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleLogoUpload}
            >
              <Button icon={<UploadOutlined />} loading={uploadingLogo}>
                Upload logo
              </Button>
            </Upload>
            {article?.share_short_name ? (
              <Button
                loading={actionLoading === 'sync'}
                onClick={handleSyncLogo}
              >
                Sync from product
              </Button>
            ) : null}
          </Space>
        ) : null}

        <Form.Item
          name="headline"
          label="Headline"
          rules={[{ required: true, message: 'Headline is required' }]}
        >
          <Input disabled={!permissions.canEdit} />
        </Form.Item>
        <Form.Item name="published_at" label="Publish date">
          <DatePicker
            className="w-full"
            format="DD MMM YYYY"
            disabled={!permissions.canEdit}
          />
        </Form.Item>
        <Form.Item name="source_name" label="Source name">
          <Input disabled={!permissions.canEdit} />
        </Form.Item>
        <Form.Item name="short_description" label="Short description">
          <Input.TextArea rows={4} disabled={!permissions.canEdit} />
        </Form.Item>
      </Form>

      <Space wrap className="mt-4">
        {permissions.canPublish && article?.status !== 'published' ? (
          <Button
            type="primary"
            loading={actionLoading === 'publish'}
            onClick={() =>
              runAction('publish', async () => {
                const values = form.getFieldsValue();
                await newsService.publishNews(article.id, {
                  published_at: values.published_at
                    ? values.published_at.toISOString()
                    : undefined,
                });
              })
            }
          >
            Publish
          </Button>
        ) : null}
        {permissions.canPublish && article?.status === 'published' ? (
          <Button
            loading={actionLoading === 'unpublish'}
            onClick={() =>
              runAction('unpublish', () => newsService.unpublishNews(article.id))
            }
          >
            Unpublish
          </Button>
        ) : null}
        {permissions.canReject && article?.status !== 'rejected' ? (
          <Button
            danger
            loading={actionLoading === 'reject'}
            onClick={() =>
              runAction('reject', () => newsService.rejectNews(article.id))
            }
          >
            Reject
          </Button>
        ) : null}
      </Space>
    </Drawer>
  );
}
